import { useModal } from "@homebound/beam";
import {
  BaseChangeEventFormInput,
  BaseChangeEventFormState,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { PurchaseOrderCleanupModal } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/components/PurchaseOrderCleanupModal";
import { WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import {
  PurchaseOrderCleanup_ChangeEventFragment,
  usePurchaseOrderCleanupChangeEventLazyQuery,
} from "./components/PurchaseOrderCleanupModal.generated";

type MaybeOpenCommitmentCleanupModalArgs<F extends BaseChangeEventFormInput> = {
  form: WorkflowFormState<F>;
  onSubmit: () => Promise<void>;
  onSaveDraftAndExit: () => Promise<void>;
};

/**
 * Because swapping a product can impact existing commitments, we need to block submitting the change event
 * while the user cleans up the commitments by hand.
 */
export function useMaybeOpenCommitmentCleanupModal<F extends BaseChangeEventFormInput>(
  args: MaybeOpenCommitmentCleanupModalArgs<F>,
) {
  const { form, onSubmit, onSaveDraftAndExit } = args;
  const { openModal } = useModal();
  const reviewForm = form as typeof form & BaseChangeEventFormState<F>;
  // no-cache then users can go back to the stepper after taking action and see fresh data
  const [fetchImpactedCommitments] = usePurchaseOrderCleanupChangeEventLazyQuery({ fetchPolicy: "no-cache" });

  return {
    submitWithCommitmentCleanup: async (): Promise<void> => {
      if (!form.canSave()) return; // Sanity check the form can be saved

      const changeEventId = reviewForm.id.value;
      if (changeEventId) {
        const { data } = await fetchImpactedCommitments({ variables: { changeEventId } });
        if (data) {
          const commitments = getImpactedCommitments(data.changeEvent);
          if (commitments.nonEmpty) {
            return openModal({
              size: "lg",
              content: (
                <PurchaseOrderCleanupModal
                  projectId={data.changeEvent.project.id}
                  commitments={commitments}
                  onSaveDraftAndExit={onSaveDraftAndExit}
                />
              ),
            });
          }
        }
      }

      await onSubmit();
    },
  };
}

/**
 * Pull all swapped out project items that have a committed cost (including drafts)
 * Then filter impacted commitments that include any of them
 */
function getImpactedCommitments(changeEvent: PurchaseOrderCleanup_ChangeEventFragment) {
  const { impactedCommitments = [], materialVariantSwaps = [] } = changeEvent;
  if (impactedCommitments.isEmpty) return [];

  const projectItemIds = materialVariantSwaps
    .map((swap) => swap.originalProjectItem)
    .filter((pi) => pi.allCommittedCostInCents > 0)
    .map((pi) => pi.id);
  return impactedCommitments.filter((c) => c.lineItems.some((cli) => projectItemIds.includes(cli.projectItem.id)));
}
